import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Plus, Trash2, Edit2, Check, X, User, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationRecipientsPanel = () => {
  const { user } = useAuth();
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newRecipient, setNewRecipient] = useState({ name: '', email: '', is_active: true });
  const [editForm, setEditForm] = useState({ name: '', email: '', is_active: true });

  useEffect(() => {
    fetchRecipients();
  }, []);

  const fetchRecipients = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cms/notification-recipients`);
      if (res.ok) {
        const data = await res.json();
        setRecipients(data);
      }
    } catch (error) {
      console.error('Error fetching recipients:', error);
      toast.error('Failed to load notification recipients');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newRecipient.name || !newRecipient.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/cms/notification-recipients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.token}`
        },
        body: JSON.stringify(newRecipient)
      });

      if (res.ok) {
        const data = await res.json();
        setRecipients([...recipients, data]);
        setNewRecipient({ name: '', email: '', is_active: true });
        setIsAdding(false);
        toast.success('Recipient added successfully');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add recipient');
      }
    } catch (error) {
      console.error('Error adding recipient:', error);
      toast.error('Failed to add recipient');
    }
  };

  const handleUpdate = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/cms/notification-recipients/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.token}`
        },
        body: JSON.stringify(editForm)
      });

      if (res.ok) {
        const updated = await res.json();
        setRecipients(recipients.map(r => r.id === id ? updated : r));
        setEditingId(null);
        toast.success('Recipient updated successfully');
      } else {
        toast.error('Failed to update recipient');
      }
    } catch (error) {
      console.error('Error updating recipient:', error);
      toast.error('Failed to update recipient');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recipient?')) return;

    try {
      const res = await fetch(`${API_URL}/api/cms/notification-recipients/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${user?.token}`
        }
      });

      if (res.ok) {
        setRecipients(recipients.filter(r => r.id !== id));
        toast.success('Recipient deleted successfully');
      } else {
        toast.error('Failed to delete recipient');
      }
    } catch (error) {
      console.error('Error deleting recipient:', error);
      toast.error('Failed to delete recipient');
    }
  };

  const toggleActive = async (recipient) => {
    try {
      const res = await fetch(`${API_URL}/api/cms/notification-recipients/${recipient.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.token}`
        },
        body: JSON.stringify({ is_active: !recipient.is_active })
      });

      if (res.ok) {
        const updated = await res.json();
        setRecipients(recipients.map(r => r.id === recipient.id ? updated : r));
        toast.success(`Recipient ${updated.is_active ? 'activated' : 'deactivated'}`);
      }
    } catch (error) {
      console.error('Error toggling recipient:', error);
      toast.error('Failed to update recipient status');
    }
  };

  const startEdit = (recipient) => {
    setEditingId(recipient.id);
    setEditForm({ name: recipient.name, email: recipient.email, is_active: recipient.is_active });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#320547] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#320547]">Notification Recipients</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage email addresses that receive inquiry notifications from the contact form
          </p>
        </div>
        <Button
          onClick={() => setIsAdding(true)}
          className="bg-[#320547] hover:bg-[#95228A] text-white"
          disabled={isAdding}
        >
          <Plus size={16} className="mr-2" />
          Add Recipient
        </Button>
      </div>

      {/* Add New Recipient Form */}
      {isAdding && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="border-[#320547]/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-[#320547]">Add New Recipient</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <Input
                    placeholder="John Doe"
                    value={newRecipient.name}
                    onChange={(e) => setNewRecipient({ ...newRecipient, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <Input
                    type="email"
                    placeholder="john@company.com"
                    value={newRecipient.email}
                    onChange={(e) => setNewRecipient({ ...newRecipient, email: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={handleAdd} className="bg-[#320547] hover:bg-[#95228A]">
                  <Check size={16} className="mr-2" />
                  Save
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsAdding(false);
                    setNewRecipient({ name: '', email: '', is_active: true });
                  }}
                >
                  <X size={16} className="mr-2" />
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Recipients List */}
      <div className="space-y-3">
        {recipients.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Mail className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">No recipients configured</h3>
              <p className="text-gray-500 mb-4">
                Add email addresses to receive inquiry notifications when users submit the contact form.
              </p>
              <Button
                onClick={() => setIsAdding(true)}
                className="bg-[#320547] hover:bg-[#95228A]"
              >
                <Plus size={16} className="mr-2" />
                Add First Recipient
              </Button>
            </CardContent>
          </Card>
        ) : (
          recipients.map((recipient) => (
            <motion.div
              key={recipient.id}
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Card className={`${!recipient.is_active ? 'opacity-60' : ''}`}>
                <CardContent className="py-4">
                  {editingId === recipient.id ? (
                    /* Edit Mode */
                    <div className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                          <Input
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                          <Input
                            type="email"
                            value={editForm.email}
                            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleUpdate(recipient.id)}
                          className="bg-[#320547] hover:bg-[#95228A]"
                        >
                          <Check size={14} className="mr-1" />
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingId(null)}
                        >
                          <X size={14} className="mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* View Mode */
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${recipient.is_active ? 'bg-[#320547]/10' : 'bg-gray-100'}`}>
                          <User size={18} className={recipient.is_active ? 'text-[#320547]' : 'text-gray-400'} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{recipient.name}</p>
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <Mail size={12} />
                            {recipient.email}
                          </p>
                        </div>
                        {!recipient.is_active && (
                          <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => toggleActive(recipient)}
                          className="text-gray-500 hover:text-[#320547]"
                          title={recipient.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {recipient.is_active ? (
                            <ToggleRight size={20} className="text-green-500" />
                          ) : (
                            <ToggleLeft size={20} className="text-gray-400" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => startEdit(recipient)}
                          className="text-gray-500 hover:text-[#320547]"
                        >
                          <Edit2 size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(recipient.id)}
                          className="text-gray-500 hover:text-red-500"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
        <div className="flex gap-3">
          <Mail className="text-blue-500 flex-shrink-0 mt-0.5" size={18} />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">How it works</p>
            <p className="text-blue-600">
              When a user submits the contact form, all <strong>active</strong> recipients will receive an email 
              with the complete inquiry details including name, email, phone, company, and message.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationRecipientsPanel;
